import React, { useEffect, useMemo, useState } from 'react'
import { handbookSelectors, handbookThunks } from '@/entity/handbook'
import { message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { DataGridTable } from '@/ui'
import styles from '@features/contracts/additionalContracts/AdditionalContractStyles.module.scss'
import { composeAddress } from '@/utils'

const dpositionCodeMessages = {
	rus: ' Указанные данные не найдены в справочнике',
	kz: ' Көрсетілген деректер анықтамалықта табылған жоқ',
}

export const AdditionalContractInformation = ({ additionalContract }) => {
	const {
		t,
		i18n: { language },
	} = useTranslation()
	const dispatch = useDispatch()
	const positions = useSelector(handbookSelectors.positions)
	const remoteWorkTypes = useSelector(handbookSelectors.remoteWorkTypes)
	const jobTypes = useSelector(handbookSelectors.jobTypes)
	const workingTimeModes = useSelector(handbookSelectors.workingTimeModes)
	const workCondition = useSelector(handbookSelectors.workCondition)
	const workActivityTypes = useSelector(handbookSelectors.workActivityTypes)
	const contractTerms = useSelector(handbookSelectors.contractTerms)
	const positionsByKnz = useSelector(handbookSelectors.positionsByKnz)

	const optionLang = useMemo(() => (language === 'rus' ? 'nameRu' : 'nameKz'), [language])

	const { dlocalityCode, dregionCode, ddistrictCode } = additionalContract
	const [locality, setLocality] = useState(additionalContract.locality)
	const [district, setDistrict] = useState(additionalContract.district)
	const [region, setRegion] = useState(additionalContract.region)
	const workType = jobTypes.find((item) => item.code == additionalContract?.dpartTimeCode)?.[language] || '-'
	const dworkingHours =
		workingTimeModes.find((item) => item.code == additionalContract?.dworkingHoursCode)?.[language] || '-'
	const getDangerListData = (index) => ({
		title: t(`contract.info.conditions${index}`),
		data: additionalContract?.dharmfulConditionsTypeCode?.startsWith(`L${index}`)
			? workActivityTypes.find((item) => item.code === additionalContract?.dharmfulConditionsTypeCode)?.[language]
			: '-',
	})

	useEffect(() => {
		const addressConfig = [
			{ field: additionalContract.locality, code: dlocalityCode, setter: setLocality, name: 'locality' },
			{ field: additionalContract.district, code: ddistrictCode, setter: setDistrict, name: 'district' },
			{ field: additionalContract.region, code: dregionCode, setter: setRegion, name: 'region' },
		]

		addressConfig.forEach(({ field, code, setter, name }) => {
			if (!field && code) {
				dispatch(
					handbookThunks.getKatoByCode({
						data: { code },
						onSuccess: (res) => setter(res[optionLang]),
						onError: (err) => {
							message.error(t('unknown_error', { name }), err)
							setter('-')
						},
					})
				)
			}
		})
	}, [])

	const contactInformation = () => {
		return [
			{ title: t('add_contract_num'), data: additionalContract?.contractNum || '-' },
			{ title: t('reg_date_additional_contract'), data: additionalContract?.registerDate || '-' },
			{ title: t('contract.info.start_date'), data: additionalContract?.dateFrom || '-' },
			{ title: t('contract.info.expiration_date'), data: additionalContract?.dateTo || '-' },
			{
				title: t('contract.info.contract_term'),
				data: (contractTerms.find(({ code }) => code === additionalContract?.dcontractDateCode) || {})[language] || '-',
			},

			...(additionalContract?.employeeRequest
				? [{ title: t('contract.info.note'), data: t('contract.info.note_body_additional') }]
				: []),
			{ title: t('contract.info.work_type'), data: workType },
			{
				title: t('contract.info.nkz_position'),
				data: additionalContract?.dpositionCode
					? (positionsByKnz.find(({ code }) => code === additionalContract?.dpositionCode) || {})[language] ||
					  dpositionCodeMessages[language]
					: '-',
			},
			{
				title: t('contract.info.position'),
				data:
					additionalContract?.establishedPost ||
					(positions.find(({ code }) => code === additionalContract?.destablishedPostCode) || {})[language] ||
					'-',
			},
			{
				title: t('contract.info.working_condition'),
				data: additionalContract?.dworkingConditionsCode
					? workCondition.find((it) => it.code === additionalContract?.dworkingConditionsCode)[language]
					: '-',
			},
			getDangerListData(1),
			getDangerListData(2),
			getDangerListData(3),

			{
				title: t('work_address'),
				data:
					composeAddress(
						{
							country: additionalContract?.country,
							region: additionalContract?.region || region,
							district: additionalContract?.district || district,
							locality: additionalContract?.locality || locality,
						},
						additionalContract?.workingPlace
					) || '-',
			},
			{ title: t('contract.info.time_mode'), data: dworkingHours },
			{ title: t('contract.info.tariff_rate'), data: additionalContract?.tariffRate || '-' },
			{
				title: t('contract.info.remote_job'),
				data: (remoteWorkTypes.find(({ code }) => code === additionalContract?.dremoteWorkCode) || {})[language] || '-',
			},
			{ title: t('change_contract_other'), data: additionalContract?.otherConditions || '-' },
		]
	}

	return (
		<DataGridTable
			title={t('additional_contract_info')}
			information={contactInformation()}
			hideSubTitle
			titleClass={styles.tableTitleCell}
		/>
	)
}
